import * as zod from 'zod';

import { HardwareConfigEquipmentMapping } from '@sb/ai/types';

import { Conditional } from '../../types';

/**
 * Base configuration for completion detection methods.
 * - For learned completion/failure: threshold is the model prediction confidence (0-1)
 * - For movement completion: threshold is the position delta in mm (e.g., 5 = 5mm)
 */
export const CompletionConfig = zod.object({
  enabled: zod.boolean().default(false),
  threshold: zod.number(),
  consecutiveCount: zod.number().int(),
});

export type CompletionConfig = zod.infer<typeof CompletionConfig>;

/**
 * Movement completion configuration.
 * threshold: position delta in mm. rotationThreshold: max rotation magnitude in degrees; if exceeded, movement completion fails.
 * gripperThreshold: if gripper delta across horizon exceeds this, movement completion fails. Default 0.1.
 */
export const MovementCompletionConfig = CompletionConfig.extend({
  rotationThreshold: zod.number().default(5), // degrees
  gripperThreshold: zod.number().default(0.1),
});

export type MovementCompletionConfig = zod.infer<
  typeof MovementCompletionConfig
>;

/**
 * Consolidated completion prediction configuration with type-specific defaults
 */
export const CompletionPredictionConfig = zod.object({
  learnedCompletion: CompletionConfig.default({
    enabled: false,
    threshold: 0.75,
    consecutiveCount: 2,
  }),
  learnedFailure: CompletionConfig.default({
    enabled: false,
    threshold: 0.75,
    consecutiveCount: 2,
  }),
  movementCompletion: MovementCompletionConfig.default({
    enabled: false,
    threshold: 5, // mm default
    consecutiveCount: 3,
    rotationThreshold: 5, // degrees default
    gripperThreshold: 0.1,
  }),
});

export type CompletionPredictionConfig = zod.infer<
  typeof CompletionPredictionConfig
>;

export default zod.object({
  gpuTaskRunnerId: zod.string(),
  modelPath: zod.string(),
  timeout: zod.number(),
  isBimanual: zod.boolean(),
  isPrimary: zod.boolean(),
  equipmentKeyToEquipmentId: zod.record(zod.string()).default({}),
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
  condition: Conditional.nullable(),
  completionPrediction: CompletionPredictionConfig.default({}),
  caption: zod.string().nullable().default(null),
  useGripperMask: zod.boolean().default(false),
  frequency: zod.number().default(10),
  stepsPerInference: zod.number().int().default(2),
  /**
   * Playback speed for unit_vectors checkpoints, stored in m/s (UI shows
   * mm/s); null = model's trained speed.
   */
  maxLinearSpeed: zod.number().nullable().default(null),
  obsHorizon: zod.number().int().default(1),
  actionExecutionMode: zod.string().default('overwrite'),
  actionInterpolationMethod: zod.string().default('pchip'),
  outputSmoothingAlpha: zod.number().default(1),
  robotControllerFrequency: zod.number().int().default(0),
  enablePredictionLogging: zod.boolean().default(false),
  lockedAxesPerRobot: zod
    .record(zod.string(), zod.array(zod.number().int().min(0).max(6)))
    .default({}),
  axisLockingFramePerRobot: zod
    .record(zod.string(), zod.enum(['world', 'wrist']))
    .default({}),
});
